import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

// Cache για τις αργίες για να αποφύγουμε συνεχείς κλήσεις στη βάση
let holidaysCache: Set<string> | null = null;
let cacheYear: number | null = null;

// Ελέγχει αν μια ημερομηνία είναι Κυριακή
export const isSunday = (date: Date): boolean => {
  return date.getDay() === 0; // 0 = Κυριακή
};

// Φορτώνει τις αργίες από τη βάση για συγκεκριμένο έτος
export const loadHolidays = async (year: number): Promise<Set<string>> => {
  if (holidaysCache && cacheYear === year) {
    return holidaysCache;
  }

  try {
    // Φορτώνουμε όλες τις αργίες για το έτος
    const { data: holidays, error } = await supabase
      .from('holidays')
      .select('date')
      .gte('date', `${year}-01-01`)
      .lte('date', `${year}-12-31`);

    if (error) {
      console.error('Error loading holidays:', error);
      return new Set();
    }

    const holidayDates = new Set(holidays?.map(h => h.date) || []);
    
    // Cache τα αποτελέσματα
    holidaysCache = holidayDates;
    cacheYear = year;
    
    console.log(`[HOLIDAYS] Loaded ${holidayDates.size} holidays for year ${year}`);
    
    return holidayDates;
  } catch (error) {
    console.error('Error fetching holidays:', error);
    return new Set();
  }
};

// Ελέγχει αν μια ημερομηνία είναι προκαθορισμένη αργία
export const isScheduledHoliday = async (date: Date): Promise<boolean> => {
  const year = date.getFullYear();
  const dateString = format(date, 'yyyy-MM-dd');
  
  const holidays = await loadHolidays(year);
  return holidays.has(dateString);
};

// Ελέγχει αν μια ημερομηνία είναι αργία (Κυριακή ή προκαθορισμένη αργία)
export const isHoliday = async (date: Date): Promise<boolean> => {
  // Όλες οι Κυριακές είναι αργίες
  if (isSunday(date)) {
    return true;
  }
  
  // Έλεγχος για προκαθορισμένες αργίες
  return await isScheduledHoliday(date);
};

// Συγχρονη έκδοση που χρησιμοποιεί cache (για περιπτώσεις που έχουν ήδη φορτωθεί οι αργίες)
export const isHolidaySync = (date: Date): boolean => {
  const year = date.getFullYear();
  
  // Όλες οι Κυριακές είναι αργίες
  if (isSunday(date)) {
    return true;
  }
  
  // Αν δεν έχουμε cache για αυτό το έτος, επιστρέφουμε false
  if (!holidaysCache || cacheYear !== year) {
    return false;
  }
  
  const dateString = format(date, 'yyyy-MM-dd');
  return holidaysCache.has(dateString);
};

// Καθαρίζει το cache (χρήσιμο όταν προστίθενται νέες αργίες)
export const clearHolidaysCache = () => {
  holidaysCache = null;
  cacheYear = null;
};

// Φορτώνει αργίες για πολλές ημερομηνίες (βελτιστοποιημένο για batch processing)
export const loadHolidaysForDateRange = async (startDate: Date, endDate: Date): Promise<Set<string>> => {
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  
  const allHolidays = new Set<string>();
  
  // Φορτώνουμε αργίες για όλα τα έτη στο εύρος
  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = await loadHolidays(year);
    yearHolidays.forEach(date => allHolidays.add(date));
  }
  
  return allHolidays;
};

// Χωρίζει μια βάρδια σε ώρες αργίας και εργάσιμες ώρες
export const splitShiftByHolidays = async (
  startDate: Date, 
  endDate: Date
): Promise<{ holidayMinutes: number; workdayMinutes: number }> => {
  let holidayMinutes = 0;
  let workdayMinutes = 0;
  
  // Αν η βάρδια είναι εντός της ίδιας ημέρας
  if (startDate.getDate() === endDate.getDate() && 
      startDate.getMonth() === endDate.getMonth() && 
      startDate.getFullYear() === endDate.getFullYear()) {
    
    const totalMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    const isHolidayDay = await isHoliday(startDate);
    
    if (isHolidayDay) {
      holidayMinutes = totalMinutes;
    } else {
      workdayMinutes = totalMinutes;
    }
    
    return { holidayMinutes, workdayMinutes };
  }
  
  // Αν η βάρδια περνάει πολλές ημέρες, σπάμε τη βάρδια σε κομμάτια ανά ημέρα
  const currentTime = new Date(startDate);
  
  while (currentTime < endDate) {
    // Βρίσκουμε το τέλος της τρέχουσας ημέρας ή το τέλος της βάρδιας
    const endOfDay = new Date(currentTime);
    endOfDay.setHours(23, 59, 59, 999);
    
    const segmentEnd = endOfDay < endDate ? endOfDay : endDate;
    const segmentMinutes = Math.round((segmentEnd.getTime() - currentTime.getTime()) / (1000 * 60));
    
    // Ελέγχουμε αν αυτή η ημέρα είναι αργία
    const isHolidayDay = await isHoliday(currentTime);
    
    if (isHolidayDay) {
      holidayMinutes += segmentMinutes;
    } else {
      workdayMinutes += segmentMinutes;
    }
    
    // Προχωράμε στην επόμενη ημέρα
    currentTime.setDate(currentTime.getDate() + 1);
    currentTime.setHours(0, 0, 0, 0);
  }
  
  return { holidayMinutes, workdayMinutes };
};

// Συγχρονη έκδοση που χρησιμοποιεί cache
export const splitShiftByHolidaysSync = (
  startDate: Date, 
  endDate: Date
): { holidayMinutes: number; workdayMinutes: number } => {
  let holidayMinutes = 0;
  let workdayMinutes = 0;
  
  // Αν η βάρδια είναι εντός της ίδιας ημέρας
  if (startDate.getDate() === endDate.getDate() && 
      startDate.getMonth() === endDate.getMonth() && 
      startDate.getFullYear() === endDate.getFullYear()) {
    
    const totalMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    const isHolidayDay = isHolidaySync(startDate);
    
    if (isHolidayDay) {
      holidayMinutes = totalMinutes;
    } else {
      workdayMinutes = totalMinutes;
    }
    
    return { holidayMinutes, workdayMinutes };
  }
  
  // Αν η βάρδια περνάει πολλές ημέρες, σπάμε τη βάρδια σε κομμάτια ανά ημέρα
  const currentTime = new Date(startDate);
  
  while (currentTime < endDate) {
    // Βρίσκουμε το τέλος της τρέχουσας ημέρας ή το τέλος της βάρδιας
    const endOfDay = new Date(currentTime);
    endOfDay.setHours(23, 59, 59, 999);
    
    const segmentEnd = endOfDay < endDate ? endOfDay : endDate;
    const segmentMinutes = Math.round((segmentEnd.getTime() - currentTime.getTime()) / (1000 * 60));
    
    // Ελέγχουμε αν αυτή η ημέρα είναι αργία
    const isHolidayDay = isHolidaySync(currentTime);
    
    if (isHolidayDay) {
      holidayMinutes += segmentMinutes;
    } else {
      workdayMinutes += segmentMinutes;
    }
    
    // Προχωράμε στην επόμενη ημέρα
    currentTime.setDate(currentTime.getDate() + 1);
    currentTime.setHours(0, 0, 0, 0);
  }
  
  return { holidayMinutes, workdayMinutes };
};